/**
 * Toggle Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 150-2
 *
 * A pill-shaped switch used to communicate binary on/off states.
 * Matches the Toggle specifications from the Crop Circle design system with:
 * - Two sizes (default, small)
 * - Optional label and supporting description
 * - Disabled styling
 * - Controlled or uncontrolled usage
 */

import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { colors, spacing, typography } from '../../tokens';

const TOGGLE_SIZES = {
  default: {
    width: 52,
    height: 28,
    padding: 2,
    knob: 24,
  },
  small: {
    width: 44,
    height: 24,
    padding: 2,
    knob: 20,
  },
} as const;

export type ToggleSize = keyof typeof TOGGLE_SIZES;

export interface ToggleProps {
  /**
   * Controlled value for the toggle
   */
  value?: boolean;

  /**
   * Initial value when used uncontrolled
   * @default false
   */
  defaultValue?: boolean;

  /**
   * Callback when toggle value changes
   */
  onValueChange?: (value: boolean) => void;

  /**
   * Optional label text shown to the right of the toggle
   */
  label?: string;

  /**
   * Optional supporting description displayed under the label
   */
  description?: string;

  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;

  /**
   * Size variant
   * @default "default"
   */
  size?: ToggleSize;

  /**
   * Test identifier
   */
  testID?: string;
}

export const Toggle: React.FC<ToggleProps> = ({
  value,
  defaultValue = false,
  onValueChange,
  label,
  description,
  disabled = false,
  size = 'default',
  testID,
}) => {
  const isControlled = typeof value === 'boolean';
  const [internalValue, setInternalValue] = React.useState(defaultValue);

  React.useEffect(() => {
    if (!isControlled) {
      setInternalValue(defaultValue);
    }
  }, [defaultValue, isControlled]);

  const isOn = isControlled ? (value as boolean) : internalValue;
  const metrics = TOGGLE_SIZES[size];

  const handleToggle = () => {
    if (disabled) {
      return;
    }

    const nextValue = !isOn;
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    onValueChange?.(nextValue);
  };

  const trackColor = React.useMemo(() => {
    if (disabled) {
      return isOn ? colors.orange[200] : colors.slate[200];
    }
    return isOn ? colors.brand.core : colors.slate[200];
  }, [disabled, isOn]);

  const knobColor = disabled ? colors.neutral[200] : colors.brand.white;
  const knobTranslate =
    metrics.width - metrics.knob - metrics.padding * 2;

  return (
    <Pressable
      style={[styles.wrapper, disabled && styles.wrapperDisabled]}
      onPress={handleToggle}
      disabled={disabled}
      hitSlop={8}
      testID={testID}
      accessibilityRole="switch"
      accessibilityState={{ checked: isOn, disabled }}
    >
      <View
        style={[
          styles.track,
          {
            width: metrics.width,
            height: metrics.height,
            borderRadius: metrics.height / 2,
            padding: metrics.padding,
            backgroundColor: trackColor,
          },
          disabled && styles.trackDisabled,
        ]}
      >
        <View
          style={[
            styles.knob,
            {
              width: metrics.knob,
              height: metrics.knob,
              borderRadius: metrics.knob / 2,
              transform: [{ translateX: isOn ? knobTranslate : 0 }],
              backgroundColor: knobColor,
            },
            disabled && styles.knobDisabled,
          ]}
        />
      </View>

      {(label || description) && (
        <View style={styles.textContainer}>
          {label && (
            <Text
              style={[
                styles.label,
                disabled && styles.labelDisabled,
              ]}
            >
              {label}
            </Text>
          )}
          {description && (
            <Text
              style={[
                styles.description,
                disabled && styles.descriptionDisabled,
              ]}
            >
              {description}
            </Text>
          )}
        </View>
      )}
    </Pressable>
  );
};

Toggle.displayName = 'Toggle';

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  wrapperDisabled: {
    opacity: 0.7,
  },
  track: {
    justifyContent: 'center',
  },
  trackDisabled: {
    opacity: 0.8,
  },
  knob: {
    shadowColor: colors.slate[800],
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 1,
  },
  knobDisabled: {
    shadowOpacity: 0,
  },
  textContainer: {
    flexShrink: 1,
  },
  label: {
    ...typography.body.mediumEmphasised,
    color: colors.slate[700],
  },
  labelDisabled: {
    color: colors.slate[500],
  },
  description: {
    ...typography.body.small,
    marginTop: spacing.xxs,
    color: colors.slate[600],
  },
  descriptionDisabled: {
    color: colors.slate[500],
  },
});


